import * as React from 'react';
import { Button, Card, Checkbox, Col, Popover, Row, Table } from 'antd';
import {
	getCurrency,
	isNotUndefined,
	nullSafeGet,
	nullSafeGetOrElse,
	onRowWithRangeSelection,
} from '../../utils/DataAccessUtils';
import {
	getDateOnlyStartOfDayFromBEDateString,
	transformTypesTracked,
} from '../../utils/DataFormatterUtils';
import {
	budgetsRestCrudThunksForBuyer,
	budgetsRestCrudThunksForSupplier,
} from '../../thunks/budget_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { useMemo } from 'react';
import { KeyValueDisplay } from '../key_value_display/KeyValueDisplay';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import BudgetHeaderDisplayRow from '../budget_header_display_row/BudgetHeaderDisplayRow';
import { generateBackLink } from '../../utils/HistoryUtils';
import { updateNavbarState } from '../../actions/navbar_actions';
import BudgetCycleComponent from '../budget_cycle_component/BudgetCycleComponent';
import moment from 'moment-timezone';
import DateTimeHover from '../date_time_component/DateTime';
import {
	CSV_EXPORT_DEFAULTS,
	defaultTimeZone,
	ROLE_TYPES,
	userTimeZone,
} from '../../utils/DataConstants';
import { BudgetDetailChart } from '../charts/BudgetDetailChart';
import BudgetAddExceptions from '../budget_add_exception/BudgetAddExceptions';
import { withUserType } from '../withUserType';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { DownloadOutlined } from '@ant-design/icons';
import { ExportToCsv } from 'export-to-csv';

const queryString = require('qs');

function BudgetDetailDetailsPage({
	budget,

	currentUser,
	match,
	history,
	location,
	loading,
	userType,

	getBudget,
	deleteBudget,
	updateNavbar,
}) {
	const [showExceptions, setShowExceptions] = React.useState(true);

	const budgetId = match.params.id;
	useMemo(() => {
		updateNavbar(`Back to Budgets`, generateBackLink(`/${userType}/admin/budgets`, location));
	}, [budgetId]);
	const currency = getCurrency({ currentUser });
	let searchString = location.search;
	if (searchString[0] === '?') {
		searchString = searchString.slice(1);
	}
	const queryParams = queryString.parse(searchString);

	const getPeriod = (months) => {
		switch (months) {
			case 1:
				return 'Monthly';
			case 3:
				return 'Quarterly';
			case 12:
			default:
				return 'Yearly';
		}
	};
	const selectedTypesTracked = nullSafeGetOrElse('accrualTypesTracked', budget, []);
	const typesTracked = [
		'pending_workorders',
		'approved_workorders',
		'pending_quotes',
		'approved_quotes',
		'pending_invoices',
		'approved_invoices',
	];
	const typesTrackedContent = typesTracked
		.filter((type) => selectedTypesTracked.includes(type))
		.map((type) => <div>{transformTypesTracked(type)}</div>);
	const problemTypesTracked = nullSafeGetOrElse('problemTypes', budget, []).map((type) => (
		<div>{nullSafeGet('name', type)}</div>
	));
	const suppliersTracked = nullSafeGetOrElse('supplierFacilities', budget, []).map((type) => (
		<div>{nullSafeGet('name', type)}</div>
	));
	let budgetDetails = useMemo(
		() =>
			loading
				? {}
				: {
						'Warning Threshold': `${parseFloat(nullSafeGet('thresholdPercentage', budget)).toFixed(
							0
						)}`.concat('%'),
						Created: (
							<DateTimeHover
								timestamp={nullSafeGet('createdAt', budget)}
								showTime={false}
								timeZone={userTimeZone}
							/>
						),
						'Start Date': (
							<DateTimeHover
								timestamp={nullSafeGet('startDate', budget)}
								showTime={false}
								timeZone={defaultTimeZone}
							/>
						),
						...(nullSafeGet('endDate', budget)
							? {
									'End Date': (
										<DateTimeHover
											timestamp={nullSafeGet('endDate', budget)}
											showTime={false}
											timeZone={defaultTimeZone}
										/>
									),
							  }
							: {}),
						...(budget.trackOnlyCompletedWorkOrders
							? { 'Track Completed Work Orders': 'Yes' }
							: {}),
						...(nullSafeGet('months', budget)
							? {
									Period: getPeriod(nullSafeGet('months', budget)),
							  }
							: {}),
						...(budget.isCapex !== undefined
							? {
									'Expense Type': nullSafeGet('isCapex', budget) ? 'CapEx' : 'Repair & Maintenance',
							  }
							: {}),
						...(budget.isPM !== undefined
							? { 'Work Order Type': nullSafeGet('isPM', budget) ? 'Planned' : 'Reactive' }
							: {}),
						...(budget.avgAccrualAmountPerWorkOrder !== undefined
							? {
									'Average Accrual Amount Per Work Order': currency.format(
										nullSafeGet('avgAccrualAmountPerWorkOrder', budget)
									),
							  }
							: {}),
				  },
		[budget]
	);

	const { budgetPerLocations } = budget;

	const everyLocationHasBudgetDefined = useMemo(
		() =>
			nullSafeGetOrElse('budgetPerLocations', budget, []).every((item) =>
				isNotUndefined(item.budget)
			),
		[budget]
	);

	const locationTableData = loading
		? []
		: budgetPerLocations
		? budgetPerLocations.map((budgetLocation) => ({
				...budgetLocation,
				budgetRemaining:
					parseFloat(budgetLocation.budget) - parseFloat(budgetLocation.budgetConsumed),
		  }))
		: [
				{
					name: 'Company Wide',
					budget: nullSafeGetOrElse('sharedBudget', budget, 0),
					budgetAccrued: nullSafeGetOrElse('budgetAccrued', budget, 0),
					budgetConsumed: nullSafeGetOrElse('budgetConsumed', budget, 0),
					budgetSpent: nullSafeGetOrElse('budgetSpent', budget, 0),
					budgetRemaining:
						parseFloat(nullSafeGetOrElse('sharedBudget', budget, 0)) -
						parseFloat(nullSafeGetOrElse('budgetConsumed', budget, 0)),
				},
		  ];

	if (showExceptions) {
		const selectedCycleValue = nullSafeGet('cycle', budget);
		const externalPayments = nullSafeGetOrElse('workOrdersManagedOutside', budget, []);

		let paymentsToAppend = externalPayments;
		if (selectedCycleValue !== 'all') {
			const cycleStartDate = nullSafeGet('cycleStartDate', budget);
			const cycleEndDate = nullSafeGet('cycleEndDate', budget);

			if (cycleStartDate && cycleEndDate) {
				paymentsToAppend = externalPayments.filter((item) => {
					const invoiceDate = nullSafeGet('invoiceDate', item);
					if (!invoiceDate) {
						return false;
					}

					// Convert all dates to EST for comparison
					// invoiceDate is non-timezone based (MM/DD/YYYY format)
					const invoiceMoment = moment(invoiceDate);
					// cycleStartDate and cycleEndDate are timezone-based, so convert them to EST and normalize to start/end of day
					const startMoment = getDateOnlyStartOfDayFromBEDateString(cycleStartDate);
					const endMoment = getDateOnlyStartOfDayFromBEDateString(cycleEndDate);

					if (!invoiceMoment.isValid() || !startMoment.isValid() || !endMoment.isValid()) {
						return false;
					}

					return invoiceMoment.isBetween(startMoment, endMoment, undefined, '[]');
				});
			}
		}

		locationTableData.unshift(
			...paymentsToAppend.map((item) => ({
				name: '--',
				budgetSpent: item.invoiceAmount,
				type: 'external_payments',
			}))
		);
	}

	const columns = [
		{
			title: 'Location',
			dataIndex: 'name',
			width: 400,
			render: (text, record) =>
				record.type === 'external_payments' ? (
					<span className="bodyregular text-character-secondary">NOT SPECIFIED</span>
				) : (
					text
				),
			sorter: (a, b) => a.name.localeCompare(b.name),
		},
		...(everyLocationHasBudgetDefined
			? [
					{
						title: 'Budget Total',
						dataIndex: 'budget',
						width: 160,
						render: (text, record) => currency.format(text),
						sorter: (a, b) => a.budget - b.budget,
					},
					{
						title: 'Remaining',
						dataIndex: 'budgetRemaining',
						width: 160,
						render: (text, record) => currency.format(text),
						sorter: (a, b) => a.budgetRemaining - b.budgetRemaining,
					},
			  ]
			: []),
		{
			title: 'Spent',
			dataIndex: 'budgetSpent',
			width: 160,
			render: (text, record) => currency.format(text),
			sorter: (a, b) => a.budgetSpent - b.budgetSpent,
			defaultSortOrder: 'descend',
		},
		{
			title: 'Accrued',
			dataIndex: 'budgetAccrued',
			width: 160,
			render: (text, record) => currency.format(text),
			sorter: (a, b) => a.budgetAccrued - b.budgetAccrued,
		},
		{
			title: '',
			dataIndex: '',
			width: 100,
			render: (text, record) =>
				record.type === 'external_payments' ? (
					<span className="bodyregular font-semibold text-character-secondary">
						External payment for a non-OpenWrench WO
					</span>
				) : (
					<Button>Show Details</Button>
				),
		},
	];
	const budgetSpent = parseFloat(nullSafeGetOrElse('budgetSpent', budget, 0));
	const sharedBudget = nullSafeGetOrElse('sharedBudget', budget, 0);
	const totalBudgetForChart =
		nullSafeGet('totalBudgetForCurrentCycle', budget) !== undefined
			? nullSafeGet('totalBudgetForCurrentCycle', budget)
			: sharedBudget || 0;

	const budgetData = {
		budget: totalBudgetForChart,
		budgetAccrued: 0,
		budgetConsumed: 0,
		budgetSpent: 0,
		cycleStartDate: nullSafeGet('cycleStartDate', budget),
		cycleEndDate: nullSafeGet('cycleEndDate', budget),
	};

	locationTableData.map((item) => {
		if (!sharedBudget && nullSafeGet('totalBudgetForCurrentCycle', budget) === undefined) {
			budgetData['budget'] = budgetData['budget'] + parseFloat(item['budget']);
		}
		budgetData['budgetAccrued'] = budgetData['budgetAccrued'] + parseFloat(item['budgetAccrued']);
		budgetData['budgetConsumed'] =
			budgetData['budgetConsumed'] + parseFloat(item['budgetConsumed']);
		budgetData['budgetSpent'] = budgetData['budgetSpent'] + parseFloat(item['budgetSpent']);
	});

	const budgetLimit = nullSafeGetOrElse(
		'budget',
		budgetData,
		nullSafeGetOrElse('sharedBudget', budget, 0)
	);
	const budgetMax = Math.max(budgetLimit, budgetSpent);
	const data =
		(budget &&
			budget.budgetSpentTimeSeriesData &&
			budget.budgetSpentTimeSeriesData.map((d, i) => ({
				day: d.dayNumber,
				dayKey: `${d.date}`,
				consumedAmount: 0,
				totalConsumedAmount: moment().diff(moment(d.date)) >= 0 ? parseFloat(d.total) : undefined,
			}))) ||
		[];
	const budgetChartConfig = {
		xAxisName: 'dayKey',
		yAxisName: 'totalConsumedAmount',
		position: 'dayKey*totalConsumedAmount',
		fillArea: false,
		cols: {
			dayKey: { alias: 'Day' },
			totalConsumedAmount: {
				min: 0,
				max: budgetMax,
				alias: 'Budget Spent',
			},
		},
		yAxisLabel: {
			textStyle: {
				fontSize: '12',
				fontFamily: 'Roboto',
				fill: 'rgba(0,0,0,0.85)',
			},
			formatter: (val, item) => {
				return currency.format(val);
			},
		},
		xAxisLabel: {
			textStyle: {
				fontSize: '12',
				fontFamily: 'Roboto',
				fill: 'rgba(0,0,0,0.85)',
			},
		},
	};

	const exportCSV = React.useCallback(() => {
		const csvTitle = `Budgets_${budget.title}`;

		const currencyID = currency.id || 'USD';
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: csvTitle,
			title: csvTitle,
			headers: everyLocationHasBudgetDefined
				? [
						'Location',
						`Budget Total (${currencyID})`,
						`Remaining (${currencyID})`,
						`Spent (${currencyID})`,
						`Accrued (${currencyID})`,
						'',
				  ]
				: ['Location', `Spent (${currencyID})`, `Accrued (${currencyID})`],
		});

		const data = locationTableData.map((item) => {
			const details =
				item.type === 'external_payments'
					? 'External payment for a non-OpenWrench WO'
					: `=HYPERLINK("${
							window.location.origin
					  }/${userType}/budgets/detail/${budgetId}/locations/${
							item.locationId || 0
					  }", "Link to Budget Details")`;

			return {
				Location: item.type === 'external_payments' ? 'NOT SPECIFIED' : item.name,
				...(everyLocationHasBudgetDefined && {
					'Budget Total': isNotUndefined(item.budget) ? parseFloat(item.budget).toFixed(2) : '',
					Remaining: isNotUndefined(item.budgetRemaining)
						? parseFloat(item.budgetRemaining).toFixed(2)
						: '',
				}),
				Spent: isNotUndefined(item.budgetSpent) ? parseFloat(item.budgetSpent).toFixed(2) : '',
				Accrued: isNotUndefined(item.budgetAccrued)
					? parseFloat(item.budgetAccrued).toFixed(2)
					: '',
				Details: details,
			};
		});

		csvExporter.generateCsv(data);
	}, [
		budget.title,
		budgetId,
		currency,
		everyLocationHasBudgetDefined,
		locationTableData,
		userType,
	]);

	return loading ? (
		<PageLoadingPlaceholder />
	) : (
		<Row style={{ margin: '8px 4px' }} gutter={8}>
			<Col className={'rowSpacing'} span={24}>
				<Row className={'items-baseline justify-between'}>
					<BudgetCycleComponent budget={budget} />
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_BUDGET}>
						<BudgetAddExceptions budget={budget} />
					</AccessPermissionChecker>
				</Row>
			</Col>
			<Col span={19}>
				<Row>
					<Col className={'rowSpacing'} span={24}>
						<BudgetHeaderDisplayRow budget={budget} currency={currency} />
					</Col>
					<Col className={'rowSpacing'} span={24}>
						<Card>
							<BudgetDetailChart
								height={500}
								chartPadding={[40, 40, 40, 40]}
								data={data || []}
								xAxisName={nullSafeGet('xAxisName', budgetChartConfig)}
								yAxisName={nullSafeGet('yAxisName', budgetChartConfig)}
								yAxisLabel={nullSafeGet('yAxisLabel', budgetChartConfig)}
								fillArea={nullSafeGet('fillArea', budgetChartConfig)}
								cols={nullSafeGet('cols', budgetChartConfig)}
								position={nullSafeGet('position', budgetChartConfig)}
								currency={currency}
								budget={budgetData}
							/>
						</Card>
					</Col>
					<Col className={'rowSpacing'} span={24}>
						<Card
							title="Budgets"
							extra={
								<div className="flex items-center gap-2">
									{nullSafeGet('workOrdersManagedOutside', budget) ? (
										<Checkbox onChange={() => setShowExceptions(!showExceptions)}>
											Hide Exceptions
										</Checkbox>
									) : null}
									<Popover content="Export CSV" trigger="hover">
										<Button
											size="large"
											icon={<DownloadOutlined />}
											className="inline-block-visible-md ml-2"
											onClick={exportCSV}
										>
											<span className="inline-block-visible-xxl">Export CSV</span>
										</Button>
									</Popover>
								</div>
							}
						>
							<Table
								onRow={onRowWithRangeSelection((record, rowIndex) => {
									return {
										onClick: (event) => {
											history.push(
												`/${userType}/budgets/detail/${budgetId}/locations/${
													record.locationId || 0
												}?${new URLSearchParams(queryParams)}`
											);
										},
									};
								})}
								dataSource={locationTableData}
								columns={columns}
								rowClassName={'cursor-pointer'}
							/>
						</Card>
					</Col>
				</Row>
			</Col>
			<Col span={5}>
				<Row>
					<Col span={24} className="rowSpacing">
						<Card title="Budget Details">
							<div style={{ padding: 0 }} className="materialCard__body">
								<div className="materialCard__supportingText">
									<KeyValueDisplay
										horizontal={false}
										keyWidth={120}
										keyValueStore={budgetDetails}
									/>
								</div>
							</div>
						</Card>
					</Col>
					<Col className={'rowSpacing'} span={24}>
						<Card title={'Accrual Types Tracked'}>
							{typesTrackedContent.length > 0 ? typesTrackedContent : 'No accrual types tracked'}
						</Card>
					</Col>
					{problemTypesTracked.length > 0 ? (
						<Col className={'rowSpacing'} span={24}>
							<Card title={'Problem Types'}>{problemTypesTracked}</Card>
						</Col>
					) : null}
					{suppliersTracked.length > 0 ? (
						<Col className={'rowSpacing'} span={24}>
							<Card title={'Suppliers'}>{suppliersTracked}</Card>
						</Col>
					) : null}
				</Row>
			</Col>
		</Row>
	);
}
const mapStateToProps = (state, ownProps) => ({
	budget: state.budgets.detail,
	loading: state.budgets.fetching,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
	location: ownProps.location,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getBudget: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? budgetsRestCrudThunksForSupplier.readOne(id)
				: budgetsRestCrudThunksForBuyer.readOne(id)
		),
	deleteBudget: (budget) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? budgetsRestCrudThunksForSupplier.delete(budget)
				: budgetsRestCrudThunksForBuyer.delete(budget)
		),
	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(BudgetDetailDetailsPage as bu)) as any
);
